/*
 * Author: Blue Lobster Private Limited
 * Copyright: Blue Lobster Private Limited
 */
import classNames from "classnames";
import { createContext, useContext, useEffect, useState } from "react";
import { Outlet } from "react-router-dom";

const ThemeContext = createContext();
const ThemeUpdateContext = createContext();

/**
 * Safely returns the document body element when available.
 * @returns {HTMLBodyElement | null} Body element or null if DOM is unavailable.
 */
const getBodyElement = () => {
	if (typeof document === "undefined") {
		return null;
	}
	return document.body || document.querySelector("body");
};

export function useTheme() {
	return useContext(ThemeContext);
}

export function useThemeUpdate() {
	return useContext(ThemeUpdateContext);
}

const ThemeProvider = ({ ...props }) => {
	const defaultTheme = {
		main: "default", //other value can be passed "clean,shady,softy"
		sidebar: "dark", //other value can be passed "light,white,theme"
		sidebarCompact: true, // Changed to true to have sidebar compact/closed by default
		sidebarVisibility: false,
		sidebarMobile: false,
		header: "white", //other value can be passed "light,dark,theme"
		skin: "light", //other value can be passed "dark"
	};
	const [theme, setTheme] = useState(defaultTheme);

	const themeUpdate = {
		uistyle: function (value) {
			setTheme({ ...theme, main: value });
		},
		sidebar: function (value) {
			setTheme({ ...theme, sidebar: value });
		},
		sidebarCompact: function (e) {
			setTheme({ ...theme, sidebarCompact: !theme.sidebarCompact });
		},
		sidebarVisibility: function (e) {
			setTheme({ ...theme, sidebarVisibility: !theme.sidebarVisibility });
		},
		sidebarHide: function (e) {
			setTheme({ ...theme, sidebarVisibility: false });
		},
		header: function (value) {
			setTheme({ ...theme, header: value });
		},
		skin: function (value) {
			setTheme({ ...theme, skin: value });
		},
		reset: function (e) {
			setTheme({
				...theme,
				main: defaultTheme.main,
				sidebar: defaultTheme.sidebar,
				header: defaultTheme.header,
				skin: defaultTheme.skin,
			});
		},
	};

	const bodyClass = classNames({
		"ft-body bg-lighter npc-default has-sidebar no-touch ft-nio-theme": true,
	});

	// biome-ignore lint/correctness/useExhaustiveDependencies: bodyClass is derived from theme state, adding it would cause infinite loops
	useEffect(() => {
		const body = getBodyElement();
		if (!body) {
			return;
		}
		body.className = bodyClass;
	}, []);

	useEffect(() => {
		const body = getBodyElement();
		if (!body) {
			return;
		}
		if (theme.main === "default") {
			body.classList.add("ui-default");
			body.classList.remove("ui-clean", "ui-shady", "ui-softy");
		}
		if (theme.main === "clean") {
			body.classList.add("ui-clean");
			body.classList.remove("ui-default", "ui-shady", "ui-softy");
		}
		if (theme.main === "shady") {
			body.classList.add("ui-shady");
			body.classList.remove("ui-default", "ui-clean", "ui-softy");
		}
		if (theme.main === "softy") {
			body.classList.add("ui-softy");
			body.classList.remove("ui-default", "ui-clean", "ui-shady");
		}
		if (theme.skin === "dark") {
			body.classList.add("dark-mode");
		} else {
			body.classList.remove("dark-mode");
		}
		if (theme.sidebarVisibility === true) {
			body.classList.add("nav-shown");
		} else {
			body.classList.remove("nav-shown");
		}
	}, [theme]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: theme is already used in closure and adding it would cause infinite loops
	useEffect(() => {
		const handleMobileSidebar = () => {
			if (window.innerWidth < 1200) {
				setTheme({ ...theme, sidebarMobile: true });
			} else {
				setTheme({ ...theme, sidebarMobile: false, sidebarVisibility: false });
			}
		};

		handleMobileSidebar();
		window.addEventListener("resize", handleMobileSidebar);
		return () => {
			window.removeEventListener("resize", handleMobileSidebar);
		};
	}, []);

	return (
		<ThemeContext.Provider value={theme}>
			<ThemeUpdateContext.Provider value={themeUpdate}>
				<Outlet />
			</ThemeUpdateContext.Provider>
		</ThemeContext.Provider>
	);
};
export default ThemeProvider;
